import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { useIntl } from 'react-intl';
import { CmdTooltip } from '@commander-services/gui-components';
import {
  getWaitingsFromAnnouncementsInPosition,
  getAlarmsFromAnnouncementsInPosition,
  getEventsFromAnnouncementsInPosition,
  convertDuration,
  getWaypointsFromAnnouncementsInPosition,
} from '../RidePlayerService';
import { IAnnouncement, IRide } from '../types';
import { IS_ALARM, IS_EVENT, IS_PAUSE, IS_WAYPOINT } from '../constants';
import EventItem from './EventItem';
import {
  activeRideIndexAtom,
  actualPlayPositionAtom,
  selectedRidesAtom,
  showDataLayerAtom,
} from '../RidePlayerState';

interface IEventsWidgetProps {
  rides: IRide[];
}

export interface IEvent {
  id: number;
  title: string;
  type: 'alarm' | 'waypoint' | 'event' | 'pause';
}

export default function EventsWidget(props: IEventsWidgetProps): JSX.Element {
  const actualPosition = useRecoilValue(actualPlayPositionAtom);
  const showContent = useRecoilValue(showDataLayerAtom);
  const { formatMessage: f } = useIntl();
  const [events, setEvents] = React.useState<IEvent[]>([]);
  const activeRideIndex = useRecoilValue<number>(activeRideIndexAtom);
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRideNumber = selectedRides[activeRideIndex];
  const rideIndex = activeRideNumber - 1;

  const handleEvents = React.useCallback(() => {
    let tempEvent: IEvent[] = [];
    if (props.rides[rideIndex] && props.rides[rideIndex].positions[actualPosition]) {
      if (
        props.rides[rideIndex].positions[actualPosition].announcements &&
        props.rides[rideIndex].positions[actualPosition].announcements.length > 0
      ) {
        const alarms = getAlarmsFromAnnouncementsInPosition(
          props.rides[rideIndex].positions[actualPosition]
        );
        const alarmsForUser: IEvent[] = alarms.map((item: IAnnouncement) => {
          return {
            id: item.id,
            title: `${item.title} ${item.duration ? `(${convertDuration(item.duration)})` : ''}`,
            type: IS_ALARM,
          };
        });
        tempEvent = [...tempEvent, ...alarmsForUser];

        const waypoints = getWaypointsFromAnnouncementsInPosition(
          props.rides[rideIndex].positions[actualPosition]
        );

        const waypointForUser: IEvent[] = waypoints.map((item: IAnnouncement) => {
          return {
            id: item.id,
            title: `${item.title} ${item.duration ? `(${convertDuration(item.duration)})` : ''}`,
            type: IS_WAYPOINT,
          };
        });
        tempEvent = [...tempEvent, ...waypointForUser];
        const waitings = getWaitingsFromAnnouncementsInPosition(
          props.rides[rideIndex].positions[actualPosition]
        );
        if (waitings) {
          const waitingsForUser: IEvent = {
            id: waitings.id,
            title: `${f({ id: waitings.title })} ${
              waitings.duration ? `(${convertDuration(waitings.duration)})` : ''
            }`,
            type: IS_PAUSE,
          };
          tempEvent = [...tempEvent, waitingsForUser];
        }
        const eventEvents = getEventsFromAnnouncementsInPosition(
          props.rides[rideIndex].positions[actualPosition]
        );
        const eventEventsForUser: IEvent[] = eventEvents.map((item: IAnnouncement) => {
          return {
            id: item.id,
            title: `${f({ id: item.title })} ${
              item.duration ? `(${convertDuration(item.duration)})` : ''
            }`,
            type: IS_EVENT,
          };
        });
        tempEvent = [...tempEvent, ...eventEventsForUser];
      }
      setEvents(tempEvent);
    }
  }, [rideIndex, actualPosition, f, props.rides]);

  React.useEffect(() => {
    handleEvents();
  }, [handleEvents]);

  const listEvents = (eventsList: IEvent[], isInTooltip: boolean): JSX.Element => {
    if (eventsList.length === 0) {
      return (
        <span>
          {f({
            id: 'widget.events.noActiveAnnouncements',
          })}
        </span>
      );
    }
    return (
      <ol style={{ minWidth: '282px', listStyleType: 'none', padding: 5, marginTop: 0 }}>
        {eventsList.map((event: IEvent, index: number) => EventItem(event, index, isInTooltip))}
      </ol>
    );
  };

  return (
    <div className="item">
      {!showContent && (
        <div
          className="b-widget b-widget--1x4 b-widget--alignLeft b-widget--carInfo"
          style={{ position: 'relative' }}
        />
      )}
      {showContent && (
        <CmdTooltip
          content={showContent ? listEvents(events, true) : []}
          placement="left"
          width={300}
          customCss={{ whiteSpace: 'nowrap', top: '73px' }}
        >
          <div
            className={`b-widget b-widget--1x4 b-widget--alignLeft ${
              !events || events.length === 0 ? 'b-widget--carInfo' : ''
            }`}
            style={{ position: 'relative' }}
          >
            {!events ||
              (events.length === 0 && (
                <i className={`e-widget-icon cmd-icon cmd-icon-notification-active `} />
              ))}
            {showContent && events && events.length > 0 && (
              <ol style={{ listStyleType: 'none', padding: 0, marginTop: '5px', width: '100%' }}>
                {events.length <= 3 &&
                  events.map((event: IEvent, index: number) => EventItem(event, index))}
                {events.length > 3 &&
                  events.map(
                    (event: IEvent, index: number) => index < 2 && EventItem(event, index)
                  )}
                {events.length > 3 && (
                  <li style={{ marginTop: '5px' }} className="bullet-gray">
                    <i
                      style={{ color: '#B50536', marginRight: '7px' }}
                      className="cmd-icon cmd-icon-notification-active"
                    />
                    <span>
                      + <span>{events.length - 2}</span>
                    </span>
                  </li>
                )}
              </ol>
            )}
            {showContent && events && events.length === 0 && (
              <>
                {' '}
                <span>
                  {f({
                    id: 'widget.events.noActiveAnnouncements',
                  })}
                </span>
              </>
            )}
          </div>
        </CmdTooltip>
      )}
    </div>
  );
}
